import { CountryCode } from '@carsys/enums/country-code'
import { normalizeUppercase } from '@cbgms/base/utils/normalizers'
import { isRequired } from '@cbgms/base/utils/validations'
import Input from '@cbgms/components/form/Material/Input'
import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { change, formValueSelector } from 'redux-form'
import { Button, Popup } from 'semantic-ui-react'
import LatitudeLongitudeInputs from './LatitudeLongitudeInputs'
import { useTranslation } from 'react-i18next'
import AddressSearch from './address-search/AddressSearch'
import { Grid } from '@mui/material'
import { IAddressSearchResolveResult } from '@cbgms/api/modules/miscellaneous/address/search'
import CountriesDropdown from '@cbgms/components/form/CountriesDropdownRedux'
import { inputBaseClasses } from '@mui/material/InputBase'
import { AddressProps } from './types'

const fieldStyle = {
  [`& .${inputBaseClasses.input}`]: {
    border: 'unset !important' // styling collision with semantic causes a double border
  }
}

interface IFormStateWithAddress {
  form: any
}

export const Address: React.FC<AddressProps> = ({
  allFieldsDisabled,
  prefix,
  validate,
  hideState,
  countryOptions,
  showLatitudeLongitude,
  countrySelectionDisabled,
  form,
  getAddressFromPostalCode,
  getLocationFromAddress,
  actions
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const selector = formValueSelector(form)
  const country: CountryCode = useSelector((state: IFormStateWithAddress) => selector(state, `${prefix}Country`))
  const houseNumber: string = useSelector((state: IFormStateWithAddress) => selector(state, `${prefix}HouseNumber`))
  const postalCode: string = useSelector((state: IFormStateWithAddress) => selector(state, `${prefix}PostalCode`))
  const address: string = useSelector((state: IFormStateWithAddress) => selector(state, `${prefix}Address`))
  const city: string = useSelector((state: IFormStateWithAddress) => selector(state, `${prefix}City`))
  const state: string = useSelector((state: IFormStateWithAddress) => selector(state, `${prefix}State`))

  const getAddress = async () => {
    try {
      if (country && country === CountryCode.NL && houseNumber && postalCode) {
        const address = await dispatch(getAddressFromPostalCode(country, houseNumber, postalCode))

        dispatch(change(form, `${prefix}Address`, address.Data.Address))
        dispatch(change(form, `${prefix}City`, address.Data.City))
        dispatch(change(form, `${prefix}State`, address.Data.State))
      }
    } catch {
      return null
    }
  }

  const onSearchAddressSelect = (a: IAddressSearchResolveResult) => {
    dispatch(change(form, `${prefix}Address`, a.Street))
    dispatch(change(form, `${prefix}City`, a.City))
    dispatch(change(form, `${prefix}State`, a.State))
    dispatch(change(form, `${prefix}HouseNumber`, a.HouseNumber))
    dispatch(change(form, `${prefix}PostalCode`, a.ZipCode))
  }

  const showLocationLookup = !!getLocationFromAddress
  const [isLoadingLocation, setIsLoadingLocation] = useState(false)

  const onLocationLookupClick = async () => {
    if (!getLocationFromAddress || isLoadingLocation) {
      return
    }

    setIsLoadingLocation(true)

    try {
      const location = await getLocationFromAddress({
        Address: address,
        City: city,
        Country: country,
        HouseNumber: houseNumber,
        PostalCode: postalCode,
        State: state
      })

      dispatch(change(form, `${prefix}Latitude`, location.Latitude))
      dispatch(change(form, `${prefix}Longitude`, location.Longitude))
    } finally {
      setIsLoadingLocation(false)
    }
  }

  return (
    <Grid container spacing={2}>
      <Grid item md={4} xs={6}>
        <CountriesDropdown
          countryOptions={countryOptions}
          sx={fieldStyle}
          label={t('global.country')}
          fullWidth
          name={`${prefix}Country`}
          required={validate}
          form={form}
          data-testid={`${prefix.toLowerCase()}-country-listbox`}
          disabled={allFieldsDisabled || !!countrySelectionDisabled}
        />
      </Grid>
      <Grid item md={8} xs={6}>
        <AddressSearch
          fullWidth
          country={country}
          onChange={onSearchAddressSelect}
          label={t('global.address_search')}
          actions={actions}
          disabled={allFieldsDisabled}
        />
      </Grid>
      <Grid item xs={4}>
        <Input
          sx={fieldStyle}
          fullWidth
          label={t('global.postal_code')}
          name={`${prefix}PostalCode`}
          inputProps={{ maxLength: 10 }}
          required={validate}
          validate={validate ? isRequired : undefined}
          onBlur={getAddress}
          normalize={normalizeUppercase}
          data-testid={`${prefix.toLowerCase()}-postalcode-input`}
          disabled={allFieldsDisabled}
        />
      </Grid>
      <Grid item xs={4}>
        <Input
          sx={fieldStyle}
          fullWidth
          label={t('global.number')}
          name={`${prefix}HouseNumber`}
          inputProps={{ maxLength: 10 }}
          required={validate}
          validate={validate ? isRequired : undefined}
          onBlur={getAddress}
          data-testid={`${prefix.toLowerCase()}-housenumber-input`}
          disabled={allFieldsDisabled}
        />
      </Grid>
      <Grid item xs={4}>
        <Input
          sx={fieldStyle}
          fullWidth
          label={t('global.addition')}
          name={`${prefix}HouseNumberAddition`}
          inputProps={{ maxLength: 25 }}
          data-testid={`${prefix.toLowerCase()}-addition-input`}
          disabled={allFieldsDisabled}
        />
      </Grid>
      <Grid item xs={4}>
        <Input
          sx={fieldStyle}
          fullWidth
          label={t('global.address_1_street')}
          name={`${prefix}Address`}
          inputProps={{ maxLength: 50 }}
          required={validate}
          validate={validate ? isRequired : undefined}
          data-testid={`${prefix.toLowerCase()}-address1-input`}
          disabled={allFieldsDisabled}
        />
      </Grid>
      <Grid item xs={4}>
        <Input
          sx={fieldStyle}
          fullWidth
          label={t('global.address_2_street')}
          name={`${prefix}AddressExtra`}
          inputProps={{ maxLength: 50 }}
          data-testid={`${prefix.toLowerCase()}-address2-input`}
          disabled={allFieldsDisabled}
        />
      </Grid>
      <Grid item xs={4}>
        <Input
          sx={fieldStyle}
          fullWidth
          label={t('global.city')}
          name={`${prefix}City`}
          inputProps={{ maxLength: 50 }}
          required={validate}
          validate={validate ? isRequired : undefined}
          data-testid={`${prefix.toLowerCase()}-city-input`}
          disabled={allFieldsDisabled}
        />
      </Grid>
      {(!hideState || showLatitudeLongitude) && (
        <Grid item xs={4}>
          {!hideState && (
            <Input
              sx={fieldStyle}
              fullWidth
              label={t('global.state')}
              name={`${prefix}State`}
              inputProps={{ maxLength: 50 }}
              data-testid={`${prefix.toLowerCase()}-state-input`}
              disabled={allFieldsDisabled}
            />
          )}
          {showLatitudeLongitude && (
            <div style={{ display: 'flex' }}>
              <LatitudeLongitudeInputs prefix={prefix} allFieldsDisabled={allFieldsDisabled} />
              {showLocationLookup && (
                <Popup
                  position='top right'
                  content={t('global.LatitudeLongitudeLookup')}
                  trigger={
                    <Button
                      icon='globe'
                      type='button'
                      onClick={onLocationLookupClick}
                      loading={isLoadingLocation}
                      style={{ alignSelf: 'flex-end', margin: '16px' }}
                      data-testid='latitude-longitude-lookup-button'
                    />
                  }
                />
              )}
            </div>
          )}
        </Grid>
      )}
    </Grid>
  )
}
